import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, X, Send, Mic, Loader2 } from 'lucide-react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useAuth } from '../contexts/AuthContext';
import { handleTutorQuery } from '../services/tutorService';
import { cleanTextForSpeech } from '../lib/utils';
import { Diagnostics, GoldenPathStep } from '../lib/diagnostics';
import { logAppError } from '../lib/errorLogger';
import { useUpdate } from '../contexts/UpdateContext';

interface GroundedTutorProps {
  courseId: string;
  lessonId: string;
  lessonContent: string;
  currentLevel: string;
  concepts: any[];
  sourceExcerpts: string[];
  quizPerformance?: any[];
  visuals?: any[];
}

export function GroundedTutor({ 
  courseId, 
  lessonId, 
  lessonContent, 
  currentLevel, 
  concepts, 
  sourceExcerpts,
  quizPerformance = [],
  visuals = []
}: GroundedTutorProps) {
  const { user } = useAuth();
  const { setBusyState } = useUpdate();
  const [isOpen, setIsOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState<{ role: 'user' | 'model'; text: string }[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentStream, setCurrentStream] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [playState, setPlayState] = useState<'idle' | 'playing' | 'paused'>('idle');
  const chatEndRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);
  const streamBufferRef = useRef('');
  const renderTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const chunksRef = useRef<string[]>([]);
  const currentChunkIndexRef = useRef(0);
  const audioTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const audioStatusRef = useRef<'idle' | 'playing' | 'paused'>('idle');

  const updateAudioStatus = (status: 'idle' | 'playing' | 'paused') => {
    console.log("Audio status transition:", audioStatusRef.current, "->", status);
    console.log("Current chunk index at transition:", currentChunkIndexRef.current);
    audioStatusRef.current = status;
    setPlayState(status);
  };

  useEffect(() => {
    setBusyState('ai_tutor', isLoading);
    return () => setBusyState('ai_tutor', false);
  }, [isLoading, setBusyState]);

  // Helper to clean and validate markdown
  const cleanMarkdown = (text: string) => {
    if (!text) return '';
    
    let cleaned = text;

    // 1. Handle double-stringified JSON or literal quotes
    try {
      if (typeof cleaned === 'string' && cleaned.startsWith('"') && cleaned.endsWith('"')) {
        const parsed = JSON.parse(cleaned);
        if (typeof parsed === 'string') {
          cleaned = parsed;
        }
      }
    } catch {
      // keep as is
    }

    // 2. Fix escaped newlines and literals (very common cause of markdown symbols showing)
    cleaned = cleaned.replace(/\\\\n/g, '\n');
    cleaned = cleaned.replace(/\\n/g, '\n');
    cleaned = cleaned.replace(/\\"/g, '"');
    
    // 2.5 Fix escaped markdown characters
    cleaned = cleaned.replace(/\\\*/g, '*');
    cleaned = cleaned.replace(/\\\_/g, '_');
    cleaned = cleaned.replace(/\\\[/g, '[');
    cleaned = cleaned.replace(/\\\]/g, ']');
    cleaned = cleaned.replace(/\\\(/g, '(');
    cleaned = cleaned.replace(/\\\)/g, ')');
    cleaned = cleaned.replace(/\\\#/g, '#');

    // 3. Fix headers that have spaces before them (breaks parsing)
    cleaned = cleaned.replace(/^[ \t]+(#{1,6})/gm, '$1');
    
    // 4. Ensure headers have a space after the symbol (e.g., "##Header" -> "## Header")
    cleaned = cleaned.replace(/^(#{1,6})([^#\s])/gm, '$1 $2');

    // 5. Ensure newline before and after headers
    cleaned = cleaned.replace(/([^\n])\n(#{1,6}\s)/g, '$1\n\n$2');
    cleaned = cleaned.replace(/(#{1,6}\s[^\n]+)\n([^\n#])/g, '$1\n\n$2');
    
    return cleaned.trim();
  };

  // Scroll to bottom
  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-GB';

      recognitionRef.current.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setInput(transcript);
        setIsListening(false);
        handleSendMessage(transcript);
      };

      recognitionRef.current.onerror = (event: any) => {
        if (event.error === 'no-speech') {
          setIsListening(false);
          return;
        }
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  }, []);

  // Clear chat when lesson or course changes
  useEffect(() => {
    setChatHistory([{ role: 'model', text: `Hi! I'm your AI tutor. Ask me anything about this lesson or document.` }]);
    setCurrentStream('');
    setIsLoading(false);
    setIsTyping(false);
    handleStop();
  }, [courseId, lessonId]);

  // Scroll to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory, currentStream, isOpen]);

  useEffect(() => {
    const handleAskTutor = (e: Event) => {
      const customEvent = e as CustomEvent<{ message: string; silent?: boolean }>;
      const { message, silent } = customEvent.detail;
      if (message) {
        setIsOpen(true);
        if (!silent) {
          handleSendMessage(message);
        } else {
          // Just add a nudge or handle it quietly if needed
          setChatHistory(prev => [...prev, { role: 'model', text: message }]);
        }
      }
    };

    window.addEventListener('askTutor', handleAskTutor);
    return () => window.removeEventListener('askTutor', handleAskTutor);
  }, [courseId, lessonId, lessonContent, currentLevel, concepts, sourceExcerpts, visuals, quizPerformance]);

  const playNextChunk = () => {
    if (currentChunkIndexRef.current >= chunksRef.current.length) {
      updateAudioStatus('idle');
      return;
    }

    const text = chunksRef.current[currentChunkIndexRef.current];
    console.log("Audio status:", audioStatusRef.current);
    console.log("Current chunk index spoken:", currentChunkIndexRef.current, "/", chunksRef.current.length, "text:", text);
    const utterance = new SpeechSynthesisUtterance(text);
    utteranceRef.current = utterance;
    (window as any)._luminaGroundedTutorUtterance = utterance;

    const voices = window.speechSynthesis.getVoices();
    const preferredVoiceUri = localStorage.getItem('lumina_preferred_voice');
    const voice = voices.find(v => v.voiceURI === preferredVoiceUri) || voices.find(v => v.lang === 'en-GB') || voices[0];
    if (voice) utterance.voice = voice;
    utterance.rate = 1.0;

    utterance.onend = () => {
      // If paused or stopped, don't auto-advance or if replaced
      if (utteranceRef.current !== utterance) return;
      if (audioStatusRef.current !== 'playing') return;
      
      currentChunkIndexRef.current++;
      audioTimeoutRef.current = setTimeout(() => {
        if (utteranceRef.current === utterance && audioStatusRef.current === 'playing') {
            playNextChunk();
        }
      }, 300);
    };

    utterance.onerror = (e: any) => {
      if (utteranceRef.current !== utterance) return;
      // In some browsers, cancel() triggers an error with isCanceled or e.error === 'canceled'
      if (e && e.error !== 'canceled') {
         updateAudioStatus('idle');
      }
    };

    window.speechSynthesis.speak(utterance);
  };

  const speakTextChunks = (text: string) => {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    if (audioTimeoutRef.current) clearTimeout(audioTimeoutRef.current);
    utteranceRef.current = null;
    
    // Stop other potential audio globally
    const event = new CustomEvent('audioConflictStart', { detail: 'tutor' });
    window.dispatchEvent(event);

    const sanitized = cleanTextForSpeech(text);
    chunksRef.current = sanitized.split(/[.?!;]+/).filter(c => c.trim().length > 0);
    currentChunkIndexRef.current = 0;
    
    updateAudioStatus('playing');
    playNextChunk();
  };

  const handlePause = () => {
    console.log("Current chunk index before pause:", currentChunkIndexRef.current);
    updateAudioStatus('paused');
    window.speechSynthesis.cancel();
    utteranceRef.current = null;
    if (audioTimeoutRef.current) clearTimeout(audioTimeoutRef.current);
    console.log("Current chunk index after pause:", currentChunkIndexRef.current);
  };

  const handleResumeOrPlay = () => {
    console.log("Current chunk index on resume:", currentChunkIndexRef.current);
    if (audioStatusRef.current === 'paused') {
      updateAudioStatus('playing');
      
      const event = new CustomEvent('audioConflictStart', { detail: 'tutor' });
      window.dispatchEvent(event);

      // Replay current chunk from beginning
      window.speechSynthesis.cancel();
      playNextChunk(); 
    } else {
        const lastModelMessage = chatHistory.filter(m => m.role === 'model').pop();
        if (lastModelMessage) {
            speakTextChunks(lastModelMessage.text);
        }
    }
  };

  const handleStop = () => {
     updateAudioStatus('idle');
     window.speechSynthesis.cancel();
     utteranceRef.current = null;
     currentChunkIndexRef.current = 0;
     if (audioTimeoutRef.current) clearTimeout(audioTimeoutRef.current);
  };

  // Listen for audio conflicts
  useEffect(() => {
    const handleConflict = (e: any) => {
      if (e.detail !== 'tutor') {
         handleStop();
      }
    };
    window.addEventListener('audioConflictStart', handleConflict);
    return () => {
      window.removeEventListener('audioConflictStart', handleConflict);
      handleStop();
    };
  }, []);

  const handleSendMessage = async (text: string) => {
    const messageText = text.trim();
    if (!messageText || isLoading || !user) return;

    setChatHistory(prev => [...prev, { role: 'user', text: messageText }]);
    setInput('');
    setIsLoading(true);
    setCurrentStream('');
    streamBufferRef.current = '';
    setIsTyping(true);

    Diagnostics.logStep(GoldenPathStep.AI_TUTOR_ASK, 'start');

    try {
      const result = await handleTutorQuery({
        userId: user.uid,
        courseId,
        lessonId,
        user_query: messageText,
        lesson_content: lessonContent,
        current_level: currentLevel,
        concepts,
        source_excerpts: sourceExcerpts,
        quizPerformance,
        visuals,
        onToken: (token) => {
          setIsLoading(false);
          // Only update buffer if we actually got content
          if (token) {
            streamBufferRef.current += token;
            setIsTyping(true);
            
            // Debounced update to prevent broken markdown display or frequent re-renders
            if (renderTimeoutRef.current) clearTimeout(renderTimeoutRef.current);
            renderTimeoutRef.current = setTimeout(() => {
              setCurrentStream(cleanMarkdown(streamBufferRef.current));
            }, 100);
          }
        }
      });

      // Clear any pending render timeouts
      if (renderTimeoutRef.current) {
        clearTimeout(renderTimeoutRef.current);
        renderTimeoutRef.current = null;
      }

      const finalResponse = cleanMarkdown(result.response);
      Diagnostics.logStep(GoldenPathStep.AI_TUTOR_ASK, 'success');
      setChatHistory(prev => [...prev, { role: 'model', text: finalResponse }]);
      setCurrentStream('');
      streamBufferRef.current = '';
      speakTextChunks(finalResponse);
    } catch (error: any) {
      console.error('Chat error:', error);
      Diagnostics.logStep(GoldenPathStep.AI_TUTOR_ASK, 'fail', { error: error.message });
      setChatHistory(prev => [...prev, { role: 'model', text: 'I encountered an error. Please try again.' }]);
      setCurrentStream('');
      logAppError('tutor_error', error.message || 'Unknown tutor error', { userId: user?.uid, courseId });
    } finally {
      setIsLoading(false);
      setIsTyping(false);
      if (renderTimeoutRef.current) {
        clearTimeout(renderTimeoutRef.current);
        renderTimeoutRef.current = null;
      }
    }
  };

  const toggleListening = () => {
    if (isListening) {
      recognitionRef.current?.stop();
    } else {
      if (recognitionRef.current) {
        setIsListening(true);
        recognitionRef.current.start();
      } else {
        alert('Speech recognition is not supported in this browser.');
      }
    }
  };

  return (
    <div className="fixed bottom-[84px] right-6 z-50 flex flex-col items-end">
      {isOpen && (
        <div className="mb-4 w-80 sm:w-96 h-[500px] max-h-[70vh] bg-bg-alt rounded-2xl shadow-2xl border border-border-main flex flex-col overflow-hidden animate-in slide-in-from-bottom-4">
          {/* Header */}
          <div className="p-4 bg-indigo-600 flex justify-between items-center text-white">
            <div className="flex items-center gap-2 font-medium">
              <MessageSquare className="h-5 w-5" />
              Grounded Tutor
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={handleResumeOrPlay}
                disabled={playState === 'playing'}
                className="text-white hover:text-emerald-300 transition-colors text-xs font-bold px-2 py-1 bg-white/10 rounded disabled:opacity-50"
                title="Play/Resume"
              >
                Play
              </button>
              <button 
                onClick={handlePause}
                disabled={playState !== 'playing'}
                className="text-white hover:text-amber-300 transition-colors text-xs font-bold px-2 py-1 bg-white/10 rounded disabled:opacity-50"
                title="Pause"
              >
                Pause
              </button>
              <button 
                onClick={handleStop}
                disabled={playState === 'idle'}
                className="text-white hover:text-rose-300 transition-colors text-xs font-bold px-2 py-1 bg-white/10 rounded mr-2 disabled:opacity-50"
                title="Stop Audio"
              >
                Stop
              </button>
              <button 
                onClick={() => {
                   setIsOpen(false);
                   handleStop();
                }} 
                className="text-white hover:opacity-80 transition-opacity"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-bg-main/50" data-clarity-mask="true">
            {chatHistory.length === 0 ? (
              <div className="text-center text-text-alt opacity-60 mt-4 text-sm">
                Hi! I'm your Grounded Tutor. Ask me anything about this course, and I'll answer based on the course materials.
              </div>
            ) : (
              chatHistory.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] p-3 rounded-2xl text-sm ${msg.role === 'user' ? 'bg-indigo-600 text-white rounded-tr-sm' : 'bg-bg-alt text-text-alt border border-border-main rounded-tl-sm'}`}>
                    <div className="prose prose-theme prose-sm max-w-none break-words text-current">
                      <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{msg.text}</Markdown>
                    </div>
                  </div>
                </div>
              ))
            )}
            {currentStream && (
              <div className="flex justify-start">
                <div className="max-w-[85%] p-3 rounded-2xl text-sm bg-bg-alt text-text-alt border border-border-main rounded-tl-sm">
                  <div className="prose prose-theme prose-sm max-w-none break-words text-current">
                    <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{currentStream}</Markdown>
                  </div>
                </div>
              </div>
            )}
            {isLoading && !currentStream && (
              <div className="flex justify-start">
                <div className="bg-bg-alt text-text-alt border border-border-main p-3 rounded-2xl rounded-tl-sm flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />
                  <span className="text-sm">Thinking...</span>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <form 
            onSubmit={(e) => { e.preventDefault(); handleSendMessage(input); }} 
            className="p-3 bg-bg-alt border-t border-border-main flex gap-2 items-center"
          >
            <button
              type="button"
              onClick={toggleListening}
              className={`p-2 rounded-xl transition-colors ${isListening ? 'bg-rose-500/20 text-rose-500 animate-pulse' : 'bg-bg-main text-text-alt hover:text-text-main border border-border-main'}`}
              title="Voice Input"
            >
              <Mic className="h-4 w-4" />
            </button>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={isListening ? "Listening..." : "Ask a question..."}
              className="flex-1 bg-bg-main border border-border-main rounded-xl px-3 py-2 text-sm text-text-main placeholder-text-alt opacity-60 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button
              type="submit"
              disabled={!input.trim() || isLoading}
              className="p-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
      
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-3 rounded-full shadow-lg flex items-center justify-center transition-all gap-2"
      >
        {isOpen ? <X className="h-5 w-5" /> : (
            <>
              <MessageSquare className="w-5 h-5" />
              <span className="font-medium whitespace-nowrap">AI Tutor</span>
            </>
        )}
      </button>
    </div>
  );
}
